import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveSessionForType } from '../../auth/resolveSession';
import { authorGroupRequest } from '../../services/groupInvitations';

const API = 'https://bf6a0hhwjk.execute-api.us-west-2.amazonaws.com/default/testinglambda';
const fields = [['firstName', 'First name'], ['middleInitial', 'Middle initial'], ['lastName', 'Last name'], ['penName', 'Pen name'], ['email', 'Email'], ['subdomain', 'Website address']];
async function identity() {
  const result = await resolveSessionForType('author', { restore: true, read: fetchAuthSession });
  const token = result.data?.tokens?.idToken;
  if (!token?.payload?.sub || !token.toString()) throw new Error('Session unavailable');
  return { sub: token.payload.sub, token: token.toString() };
}
export default function Profile() {
  const [state, setState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let dispose = () => {};
    const load = () => {
      dispose();
      let active = true;
      const controller = new AbortController();
      setState({ loading: true });
      const timer = setTimeout(() => {
        if (!active) return;
        active = false;
        controller.abort();
        setState({ error: 'Loading your profile took too long. Please retry.' });
      }, 20000);
      dispose = () => { active = false; controller.abort(); clearTimeout(timer); };
      (async () => {
        try {
          const owner = await identity();
          if (!active) return;
          const response = await fetch(API, { headers: { Authorization: 'Bearer ' + owner.token }, signal: controller.signal });
          if (!response.ok) throw new Error('Profile unavailable');
          const data = await response.json();
          if (!data || typeof data !== 'object' || Array.isArray(data) || fields.some(([key]) => data[key] != null && typeof data[key] !== 'string')) throw new Error('Invalid profile');
          if (!active || (await identity()).sub !== owner.sub) throw new Error('Account changed');
          if (!active) return;
          clearTimeout(timer);
          setState({ profile: data, groupLoading: true });
          let groupTimer;
          try {
            const group = await Promise.race([authorGroupRequest(), new Promise((_, reject) => { groupTimer = setTimeout(() => reject(new Error('Timeout')), 20000); })]);
            if (!group || typeof group !== 'object' || !Object.prototype.hasOwnProperty.call(group, 'membership') || (group.membership != null && (typeof group.membership !== 'object' || Array.isArray(group.membership) || typeof group.membership.groupName !== 'string' || typeof group.membership.connectionStatus !== 'string'))) throw new Error('Invalid membership');
            if (!active) return;
            if ((await identity()).sub !== owner.sub) { if (active) setState({ error: 'Your account changed. Reload your profile.' }); return; }
            if (active) setState({ profile: data, membership: group.membership });
          } catch {
            if (!active) return;
            try {
              if ((await identity()).sub !== owner.sub) throw new Error('Account changed');
              if (active) setState({ profile: data, groupError: true });
            } catch { if (active) setState({ error: 'Your account changed or your session ended. Reload your profile.' }); }
          }
          finally { clearTimeout(groupTimer); }
        } catch {
          if (active) { clearTimeout(timer); setState({ error: 'We couldn’t load your Author profile. Retry, or contact Lady Liza if this continues.' }); }
        }
      })();
    };
    const visible = () => { if (document.visibilityState === 'visible') load(); };
    load();
    window.addEventListener('focus', visible);
    document.addEventListener('visibilitychange', visible);
    return () => { dispose(); window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible); };
  }, [retry]);
  const retryButton = <button type="button" onClick={() => setRetry(value => value + 1)} className="min-h-[44px] py-2 underline underline-offset-4">Retry profile</button>;
  const membership = state.membership;
  return <main className="min-h-screen bg-bookish-lightCream px-4 py-8 text-bookish-purple sm:px-6">
    <section aria-labelledby="profile-title" className="max-w-2xl mx-auto rounded-2xl border border-bookish-purple/15 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4"><User aria-hidden="true" className="h-6 w-6" /><h1 id="profile-title" className="text-2xl font-serif font-bold">Author Profile</h1></div>
      <p className="mb-6">Your saved Author account details. Contact Lady Liza if anything needs correcting.</p>
      {state.loading && <p role="status">Loading your profile…</p>}
      {state.error && <div role="alert"><p>{state.error}</p>{retryButton}</div>}
      {state.profile && <>
        <dl>{fields.map(([key, label]) => <div key={key} className="py-3 border-b border-bookish-purple/10 sm:flex sm:gap-4"><dt className="font-semibold sm:w-40 shrink-0">{label}</dt><dd className="break-words min-w-0">{state.profile[key] || 'Not provided'}</dd></div>)}</dl>
        <div className="mt-5"><h2 className="font-serif text-lg font-semibold">Publishing group</h2>
          {state.groupLoading ? <p role="status">Loading group membership…</p> : state.groupError ? <div role="alert"><p>Group membership is unavailable.</p>{retryButton}</div> : membership && membership.connectionStatus !== 'removed' ? <><Link className="inline-block py-3 underline break-words" to="/publishing-groups/author-membership">{membership.groupName || 'View group membership'}</Link>{membership.connectionStatus === 'pending' && <p>Invitation accepted — Author membership activation pending.</p>}</> : <p>No connected publishing group.</p>}
        </div>
      </>}
      <nav aria-label="Profile navigation" className="mt-6 flex flex-wrap gap-x-6"><Link className="min-h-[44px] py-3 underline" to="/authorportal/mainpage">Back to Author Portal</Link><Link className="min-h-[44px] py-3 underline" to="/authorportal/mailcenterpageuc">Contact Lady Liza</Link></nav>
    </section>
  </main>;
}
